import { Dispatch, use, useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

import OrganisationUnitScenariosResponse from 'features/residents/models/organisation-unit-scenario-response';
import Resident from 'features/residents/models/resident';

export type ResidentDetailState = {
  residentSettingEditForm: UseFormReturn<OrganisationUnitScenariosResponse>;
  viewingMode: ViewingMode;
  currentResidentSetting?: Resident;
  isDevicesAccordionExpanded: boolean;
  setIsDevicesAccordionExpanded: Dispatch<React.SetStateAction<boolean>>;
  toggleDetailViewMode: (resident: Resident) => void;
  openDetailsInViewingMode: (resident: Resident) => void;
  enableDetailViewingMode: () => void;
  enableDetailEditMode: () => void;
  closeDetails: (func?: () => void) => void;
  clearValidationErrorForScenario: (index: number) => void;
  disablePopUpGuard: () => void;
  updateCurrentResidentSetting: (resident: Resident) => void;
  resetForm: (resident: Resident) => void;
};

type Props = {
  residentSettings: Resident[];
  residentSettingsLastUpdatedAt: number;
};

const useResidentSettingDetailEditState = ({ residentSettings, residentSettingsLastUpdatedAt }: Readonly<Props>): ResidentDetailState => {
  const { showDefaultConfirmationPopUp, enablePopUpGuard } = use(ConfirmationPopupProviderContext);

  const residentSettingEditForm = useForm<OrganisationUnitScenariosResponse>({
    mode: 'onBlur',
    defaultValues: {
      organisationUnitId: '',
      organisationUnitScenarios: [],
      devices: [],
      note: '',
      scenariosMuted: false,
      muteScenariosFrom: undefined,
      muteScenariosUntil: undefined,
      muteScenariosReason: undefined,
    },
  });

  const [viewingMode, setViewingMode] = useState<ViewingMode>('none');
  const [currentResidentSetting, setCurrentResidentSetting] = useState<Resident | undefined>();
  const [isDevicesAccordionExpanded, setIsDevicesAccordionExpanded] = useState<boolean>(false);

  const clearValidationErrorForScenario = (index: number): void => {
    residentSettingEditForm.clearErrors(`organisationUnitScenarios.${index}`);
    residentSettingEditForm.trigger(`organisationUnitScenarios.${index}`);
  };

  const resetForm = (residentSetting: Resident): void => {
    residentSettingEditForm.reset({
      organisationUnitId: residentSetting.organisationUnitId,
      organisationUnitScenarios: residentSetting.organisationUnitScenarios,
      devices: residentSetting.devices,
      note: residentSetting.note,
      scenariosMuted: residentSetting.scenariosMuted,
      muteScenariosFrom: residentSetting.muteScenariosFrom ?? undefined,
      muteScenariosUntil: residentSetting.muteScenariosUntil ?? undefined,
      muteScenariosReason: residentSetting.muteScenariosReason ?? undefined,
    });
  };

  const toggleDetailViewMode = (residentSetting: Resident): void => {
    if (currentResidentSetting !== residentSetting) {
      setCurrentResidentSetting(residentSetting);
      setViewingMode('viewing');
    } else {
      closeDetails();
    }
  };

  const openDetailsInViewingMode = (residentSetting: Resident): void => {
    setCurrentResidentSetting(residentSetting);
    setViewingMode('viewing');
  };

  const handleCloseDetails = (): void => {
    setViewingMode('none');
    setCurrentResidentSetting(undefined);
    residentSettingEditForm.reset();
  };

  const closeDetails = (func?: () => void): void => {
    showDefaultConfirmationPopUp({
      shouldShow: residentSettingEditForm.formState.isDirty,
      action: () => {
        if (func) func();
        handleCloseDetails();
      },
    });
  };

  const enableDetailEditMode = (): void => setViewingMode('editing');
  const enableDetailViewingMode = (): void => setViewingMode('viewing');

  const disablePopUpGuard = (): void => {
    residentSettingEditForm.reset();
  };

  const updateCurrentResidentSetting = (resident: Resident): void => {
    setCurrentResidentSetting(resident);
  };

  useEffect(() => {
    if (currentResidentSetting) {
      resetForm(currentResidentSetting);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentResidentSetting]);

  useEffect(() => {
    if (currentResidentSetting) {
      const updatedCurrentResident = residentSettings.find(
        (resident) => resident.organisationUnitId === currentResidentSetting.organisationUnitId,
      );

      if (updatedCurrentResident) {
        setCurrentResidentSetting(updatedCurrentResident);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residentSettings, residentSettingsLastUpdatedAt]);

  useEffect(() => {
    enablePopUpGuard(residentSettingEditForm.formState.isDirty);
  }, [residentSettingEditForm.formState.isDirty]);

  return {
    residentSettingEditForm,
    viewingMode,
    currentResidentSetting,
    isDevicesAccordionExpanded,
    setIsDevicesAccordionExpanded,
    toggleDetailViewMode,
    openDetailsInViewingMode,
    enableDetailEditMode,
    enableDetailViewingMode,
    closeDetails,
    clearValidationErrorForScenario,
    disablePopUpGuard,
    updateCurrentResidentSetting,
    resetForm,
  };
};

export default useResidentSettingDetailEditState;
